import { ReactNode, useRef } from 'react'
import { useGSAP } from '@gsap/react'
import gsap from 'gsap'

import Media from '@/components/base/media'
import Section from '@/components/base/section'
import Typography from '@/components/base/typography'
import { useLenis } from '@/contexts/lenis'
import { useWindowSizeOnWidthChange } from '@/hooks/useWindowSizeOnWidthChange'

import { cn } from '@/lib/utils'

const RenderMenu = ({ children }: { children: ReactNode }) => {
  return (
    <div
      className={cn(
        'flex',
        'ml-0 h-auto w-full flex-col items-start gap-[2rem] px-[3rem]',
        'lg:ml-[20rem] lg:h-[50%] lg:w-auto lg:flex-row lg:items-center lg:gap-0 lg:px-0'
      )}
    >
      {children}
    </div>
  )
}

const Tarot = ({ img, variant }: { img: string; variant: string }) => {
  const imgRef = useRef<HTMLImageElement>(null!)
  const { lenis } = useLenis()
  const { width: winWidth } = useWindowSizeOnWidthChange()

  useGSAP(
    () => {
      if (winWidth <= 1023) {
        gsap.set(imgRef.current, {
          x: 0,
          y: 0,
          rotation: variant === 'big' ? -20 : 10
        })

        return
      }
      if (!lenis) return

      gsap.fromTo(
        imgRef.current,
        {
          y: variant === 'big' ? '20%' : '-10%',
          rotation: variant === 'big' ? 20 : -20,
          x: variant === 'big' ? '50%' : '-50%',
          transformOrigin: '50% 50%'
        },
        {
          scrollTrigger: {
            trigger: imgRef.current,
            start: 'left 100%',
            end: 'right 0%',
            horizontal: true,
            scrub: true,
            invalidateOnRefresh: true
          },
          y: variant === 'big' ? '-10%' : '20%',
          rotation: variant === 'big' ? -20 : 20,
          x: variant === 'big' ? '-50%' : '50%',
          transformOrigin: '50% 50%',
          duration: 0.5,
          ease: 'none'
        }
      )

      return () => {
        gsap.set(imgRef.current, {
          x: 0,
          y: 0,
          rotation: 0,
          clearProps: 'all'
        })
      }
    },
    {
      scope: imgRef,
      dependencies: [lenis, variant, winWidth],
      revertOnUpdate: true
    }
  )

  return (
    <div
      className={cn(
        'relative -z-[1] mx-auto my-[-3rem] w-[200px]',
        'lg:mx-0 lg:my-0 lg:inline-block lg:w-auto'
      )}
    >
      <img
        ref={imgRef}
        src={img}
        alt="tarot"
        data-motion-image
        className={cn('h-auto w-full')}
      />
    </div>
  )
}

type ItemProps = {
  number: number
  text: string
  className?: string
  big?: boolean
  index: number
}

const Item = ({ number, text, className, big = true, index }: ItemProps) => {
  const itemRef = useRef<HTMLDivElement>(null!)
  const childrenRef = useRef<HTMLDivElement>(null!)
  const { lenis } = useLenis()
  const { width: winWidth } = useWindowSizeOnWidthChange()

  useGSAP(
    () => {
      if (winWidth <= 1023 || !lenis) {
        gsap.set(itemRef.current, {
          x: 0,
          marginRight: 'auto',
          width: 'auto'
        })

        return
      }

      const width = childrenRef.current.offsetWidth

      gsap.set(itemRef.current, {
        width: width * 0.8
      })

      gsap.fromTo(
        childrenRef.current,
        {
          x: index % 2 === 0 ? '-20%' : index % 2 === 1 ? '20%' : '0%'
        },
        {
          x: index % 2 === 0 ? '20%' : index % 2 === 1 ? '10%' : '-20%',
          ease: 'none',
          scrollTrigger: {
            trigger: itemRef.current,
            start: 'left 100%',
            end: 'right 0%',
            horizontal: true,
            scrub: true,
            invalidateOnRefresh: true
          }
        }
      )

      return () => {
        gsap.set(itemRef.current, {
          x: 0,
          marginRight: 'auto',
          width: 'auto',
          clearProps: 'all'
        })
      }
    },
    {
      scope: itemRef,
      dependencies: [lenis, winWidth, index]
    }
  )

  return (
    <div
      className={cn(
        index % 3 === 0 && 'lg:self-center',
        index % 3 === 1 && 'lg:self-start',
        index % 3 === 2 && 'lg:self-end',
        className
      )}
      ref={itemRef}
    >
      <div
        className={cn(
          'flex',
          'ml-0 flex-row gap-4 self-start',
          'lg:flex-col lg:gap-0'
        )}
        ref={childrenRef}
      >
        <Typography
          className={cn(
            'inline-block font-light !leading-[1] text-white/40',
            'w-[20px] flex-shrink-0 text-right lg:w-auto lg:text-left',
            'whitespace-normal lg:whitespace-nowrap',
            big
              ? 'text-[2.4rem] lg:text-[3.6rem]'
              : 'text-[2.4rem] lg:text-[7.2rem]'
          )}
        >
          {number}
        </Typography>
        <Typography
          className={cn(
            'inline-block font-light !leading-[1] text-white',
            'text-[2.4rem] lg:text-[7.2rem]',
            'whitespace-normal lg:whitespace-nowrap',
            big
              ? 'text-[2.4rem] lg:text-[7.2rem]'
              : 'text-[2.4rem] lg:text-[3.6rem]'
          )}
        >
          {text}
        </Typography>
      </div>
    </div>
  )
}

const Menu = ({ ...props }) => {
  const root = useRef<HTMLDivElement>(null!)

  const index = useRef(-1)

  const renderList = (list: [], big = true) => {
    return list?.map((item: any, i: number) => {
      index.current += 1
      return (
        <Item
          key={`item-${i}`}
          number={item.number}
          text={item.text}
          big={big}
          index={i}
        />
      )
    })
  }

  const renderBig = () => {
    // Divido props.list in due parti
    const firstList = props.list?.slice(0, Math.ceil(props.list.length / 2))
    const secondList = props.list?.slice(Math.ceil(props.list.length / 2))

    return (
      <RenderMenu>
        {renderList(firstList, true)}
        <Tarot img={props.tarot_image.url} variant={props.variant} />
        <div className="w-0 lg:w-[200px]"></div>
        {renderList(secondList, true)}
      </RenderMenu>
    )
  }

  const renderSmall = () => {
    return (
      <RenderMenu>
        {renderList(props.list, false)}
        <Tarot img={props.tarot_image.url} variant={props.variant} />
      </RenderMenu>
    )
  }

  return (
    <Section asChild {...props} ref={root}>
      <div
        className={cn(
          'relative z-10 flex items-center',
          'h-auto flex-col',
          'lg:h-full lg:flex-row'
        )}
      >
        <div
          className={cn(
            'size-[90px] translate-x-0 translate-y-[-50%]',
            'flex items-center justify-center lg:size-[225px] lg:translate-x-[-50%] lg:translate-y-0'
          )}
        >
          <Media
            fill
            fit="contain"
            use_video={false}
            image={props.name_image}
            data-motion-media
          />
        </div>
        {props.variant === 'big' ? renderBig() : renderSmall()}
      </div>
    </Section>
  )
}

export default Menu
